// @flow

import R from 'ramda';

import type { Row } from '../../../../main/types/tableTypes';
import { roundToFixed2 } from '../../../../main/support/utils';

/* --------------INTENDED ONLY AS HELPERS FOR OTHER UTILS--------------------
------------(you will not need to import these unless testing)--------------*/

/**
 * Helper function to calculate avg based on previous and current avg/total_count
 *
 * @param {number} pAvgHours   - previous iterations average hours
 * @param {number} pTotalCount - previous event total_count value
 * @param {number} cHours - current hours to last event
 * @param {number} cEventCount - current event total_count
 * @return {number} update averages hours
 */
export function calculateNewAvgHoursFromEvent(
  pAvgHours: number,
  pTotalCount: number,
  cHours: number,
  cEventCount: number
): number {
  if (R.isNil(cHours)) {
    return pAvgHours;
  }
  if (R.isNil(pAvgHours)) {
    return cHours / cEventCount;
  }

  const pTotalHours = pAvgHours * pTotalCount;
  const cTotalHours = cHours * cEventCount;
  const totalCount = pTotalCount + cEventCount;

  const updatedAvgHours = (pTotalHours + cTotalHours) / totalCount;

  return updatedAvgHours;
}

/**
 * Helper function to calculate ratio based on previous ratio, previous total count (denom), and the incoming count
 *
 * @param {number} pRatio   - previous iterations ratio
 * @param {number} pTotalCount - previous event total_count value
 * @param {number} cCount - current count for ratio. NOTE: this is not always equivalent to current event total_count
 * @param {number} cTotalCount - current event total_count + previous event total_count
 * @return {number} update ratio
 */
export function calculateNewRatio(
  pRatio: number = 0,
  pTotalCount: number = 0,
  cCount: number,
  cTotalCount: number
): number {
  // for the null cases. undefined cases handled in default param values
  pRatio = !pRatio ? 0 : pRatio;
  pTotalCount = !pTotalCount ? 0 : pTotalCount;

  const pCount = pRatio * pTotalCount;
  return cTotalCount !== 0 ? (cCount + pCount) / cTotalCount : 0;
}

const TOUCH_TO_EVENT_BUCKET_MAX = {
  seconds_from_last_attributed_touch_to_event: 60,
  days_from_last_attributed_touch_to_event: 90
};

/**
 * Helper function to generate name new touch to event dimension name e.g. eo_install_seconds_from_last_attributed_touch_to_event_under_10
 *
 * @param {number} dataSourceName      - name of data source
 * @param {Array<number>} timeInterval - time interval that return value represents
 * @param {string} touchToEventName    - name of attributed touch to event dimension e.g. seconds_from_last_attributed_touch_to_event
 * @return {string} generated dimension name used by transformer
 */
export function generateTouchToEventName(
  dataSourceName: string,
  timeInterval: Array<number>,
  touchToEventName: string
): string {
  const base = `${dataSourceName}_${touchToEventName}`;
  if (timeInterval[0] < 1) {
    return `${base}_under_${timeInterval[1] + 1}`;
  }
  if (timeInterval[1] >= TOUCH_TO_EVENT_BUCKET_MAX[touchToEventName]) {
    return `${base}_over_${timeInterval[0]}`;
  }
  return `${base}_between_${timeInterval[0]}_and_${timeInterval[1] + 1}`;
}

/**
 * Helper function that checks whether a number falls within a range of numbers
 *
 * @param {number} touchToEventValue   - number being checked
 * @param {Array<number>} timeInterval - time interval that return value represents
 * @return {boolean} whether touchToEventValue was contained within timeInterval including the start value and the end value
 */
export function checkTouchValueWithinInterval(
  touchToEventValue: number,
  timeInterval: Array<number>
): boolean {
  return (
    (touchToEventValue - timeInterval[0]) *
      (touchToEventValue - timeInterval[1]) <=
    0
  );
}

/**
 * Helper function that returns an updated ratio given whether the touch to event dimension value in question falls within a certain time interval
 *
 * @param {number} previousTouchToEventRatio - previous ratio
 * @param {number} previousTotalCount        - previous event total_count value
 * @param {number} currentCount              - current event total_count value
 * @param {number} touchToEventValue         - current event touch to event value in question
 * @param {Array<number>} timeInterval       - a start and end value
 * @return {number} updated ratio
 */
export function generateCurrentRatio(
  previousTouchToEventRatio: number = 0,
  previousTotalCount: number = 0,
  currentCount: number = 0,
  touchToEventValue: number = 0,
  timeInterval: Array<number>
): number {
  const currentTotalCount = previousTotalCount + currentCount;
  const isTouchValueWithinInterval = checkTouchValueWithinInterval(
    touchToEventValue,
    timeInterval
  );
  currentCount = isTouchValueWithinInterval ? currentCount : 0;
  return calculateNewRatio(
    previousTouchToEventRatio,
    previousTotalCount,
    currentCount,
    currentTotalCount
  );
}

/**
 * Creates a transformer function for given data source
 *
 * @param {string} dataSourceName       - name of the data source to transform
 * @param {Object} previousValueStorage - object for storaging previous avg hours and event count
 * @return {function} transformer function for given data source
 */
export function getHoursFromEventTransformer(dataSourceName: string): Object {
  return ({ event = {}, metric = 'total_count', row = {} }) => {
    const hoursFromLastColumnName = `${dataSourceName}_hours_from_last_attributed_touch_to_event`;

    const currentCount = +event[metric] || 0;
    const updatedCount = (row[dataSourceName] || 0) + currentCount;

    const updatedAvgHours = calculateNewAvgHoursFromEvent(
      row[hoursFromLastColumnName],
      row[dataSourceName],
      event.hours_from_last_attributed_touch_to_event,
      currentCount
    );

    return {
      // TODO remove after DATA-1440 complete
      [dataSourceName]: Math.round(parseFloat(updatedCount)),
      [`${dataSourceName}_hours_from_last_attributed_touch_to_event`]: updatedAvgHours
    };
  };
}

/**
 * Creates a transformer function given a data source and attributed touch to install time intervals
 *
 * @param {string} dataSourceName - name of the data source to transform
 * @param {Object} buckets        - an Object with keys as touch_to_event dimensions and values as the desired time intervals for these dimensions
 * {
 *    seconds_from_last_attributed_touch_to_event: [[0, 9], [10, 29], [30, 60]],
 *    hours_from_last_attributed_touch_to_event: [[0, 1], [1, 90]] - assuming 90 is the upper bound for days
 * }
 * @return {function} transformer function for given data source
 */
export function getTouchToEventBucketTransformer(
  dataSourceName: string,
  buckets: Object
): Object {
  return ({ event = {}, metric = 'total_count', row = {} }) => {
    let currentCount = !event[metric] || event[metric] < 0 ? 0 : +event[metric];
    const previousTotalCount = row[dataSourceName] || 0;
    const currentTotalCount = previousTotalCount + currentCount;

    return Object.keys(buckets).reduce(
      (dimensions, bucketKey) => {
        return R.merge(
          dimensions,
          buckets[bucketKey].reduce((acc, timeInterval) => {
            const touchToEventName = generateTouchToEventName(
              dataSourceName,
              timeInterval,
              bucketKey
            );
            // we do not want to count a value if the bucket is null i.e. if seconds_from_last_attributed_touch_to_event is null
            currentCount = R.isNil(event[bucketKey]) ? 0 : currentCount;
            const currentRatio = generateCurrentRatio(
              row[touchToEventName],
              previousTotalCount,
              currentCount,
              +event[bucketKey] || 0,
              timeInterval
            );
            return R.merge(acc, { [touchToEventName]: currentRatio });
          }, {})
        );
      },
      { [dataSourceName]: currentTotalCount }
    );
  };
}

/* ---------------------Reusable Druid TRANSFORMERS----------------------------
---------(these are the transformers you are intended to import)-------------*/

/*
 * These transformers are functions that are passed the event from populateRowsFromGroupBy
 * Some wrap the transformer in another function, so you can pass them other
 * arguments as well. They should return an object with the key of the data source
 * specified, and the value is the transformer function itself.
 *
 * Note that each transformer function is passed an object as an argument,
 * containing the keys: { event, metric, dimensions, and row }.
 *
*/

/**
 * Generates transformers for calculating the avg hours from last event
 * Values need to be recalculated everytime another event with same dimensions is transformed
 * Currently used for eo_install and eo_reinstall
 *
 * @param {Array} dataSourceNames - list events to transform ['eo_install', 'eo_reinstall']
 * @return {Object} object containing transformer functions
 * {
 *   eo_install: (event, dimensions) => {
 *     ...
 *   }
 * }
 */
export function generateHoursFromEventTransformers(
  dataSourceNames: string[]
): Object {
  return dataSourceNames.reduce((transformers, dataSourceName) => {
    transformers[dataSourceName] = getHoursFromEventTransformer(dataSourceName);

    return transformers;
  }, {});
}

export function generateTouchToEventTransformers(
  dataSourceNames: string[],
  touchToEventBucketsByDataSource: Object
): Object {
  return dataSourceNames.reduce((transformers, dataSourceName) => {
    transformers[dataSourceName] = getTouchToEventBucketTransformer(
      dataSourceName,
      touchToEventBucketsByDataSource[dataSourceName]
    );
    return transformers;
  }, {});
}

type TransformerPayload = {
  event: Object,
  metric: string,
  row: Row
};

/**
 * Transformers for druid standard datasources. These are global and should only
 * be amended/appended to if we're confident that we want the transformer globally.
 * Otherwise pass transform in as a query param (see @hydrateRowsFromDataSources)
 *
 * IMPORTANT: must check row for existing value or else it may get overwritten.
 * this happens when there is a dimension that is added to query but not added to
 * the table. (e.g. hours_from_last_attributed_touch_to_event)
 */
export const STANDARD_DATASOURCE_TRANSFORMERS = {
  eo_commerce_event: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase();
    const count = (row[columnKey] || 0) + (+event[metric] || 0);

    let revenueEvent = {};
    // Only use PURCHASE revenue for revenue values
    if (columnKey === 'PURCHASE') {
      const revenue = (row.revenue || 0) + (+event.revenue || 0);
      revenueEvent = { revenue: roundToFixed2(revenue) };
    }

    return {
      // TODO remove after DATA-1440 complete
      [columnKey]: Math.round(parseFloat(count)),
      ...revenueEvent
    };
  },

  eo_custom_event: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }
    const columnKey = `eo_custom_event-${event.name}`;
    const count = (row[columnKey] || 0) + (+event[metric] || 0);

    return {
      [columnKey]: count
    };
  },

  eo_content_event: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const count = (row[event.name] || 0) + (+event[metric] || 0);

    return {
      [event.name]: count
    };
  },

  eo_user_lifecycle_event: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const count = (row[event.name] || 0) + (+event[metric] || 0);

    return {
      [event.name]: count
    };
  }
};

export const STANDARD_BLOCKED_DATASOURCE_TRANSFORMERS = {
  eo_commerce_event_blocked: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';
    const count = (row[columnKey] || 0) + (+event[metric] || 0);

    let revenueEvent = {};
    // Only use PURCHASE revenue for revenue values
    if (columnKey === 'PURCHASE_BLOCKED') {
      const revenue = (row.revenue || 0) + (+event.revenue || 0);
      revenueEvent = { revenue_blocked: roundToFixed2(revenue) };
    }

    return {
      // TODO remove after DATA-1440 complete
      [columnKey]: Math.round(parseFloat(count)),
      ...revenueEvent
    };
  },

  eo_custom_event_blocked: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }
    const columnKey = `eo_custom_event_blocked-${event.name}`;
    const count = (row[columnKey] || 0) + (+event[metric] || 0);

    return {
      [columnKey]: count
    };
  },

  eo_content_event_blocked: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';
    const count = (row[event.name] || 0) + (+event[metric] || 0);

    return {
      [columnKey]: count
    };
  },

  eo_user_lifecycle_event_blocked: function({
    event = {},
    metric = 'total_count',
    row = {}
  }: TransformerPayload): Object {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';
    const count = (row[event.name] || 0) + (+event[metric] || 0);

    return {
      [columnKey]: count
    };
  }
};

export const COST_TRANSFORMER = {
  cost: function({ event = {}, row = {} }: TransformerPayload): Object {
    const cost = (row.cost || 0) + (+event.cost || 0);
    return { cost };
  }
};
